import { Command, CommandGroup, CommandItem, CommandList } from '@trellis/roots/command'
import { Input } from '@trellis/roots/input'
import { Popover, PopoverAnchor, PopoverContent } from '@trellis/roots/popover'
import { computeLineItemSubtotal } from '@trellis/shared'
import { useEffect, useId, useState } from 'react'

import { useLineItemSuggestionsQuery } from '@/hooks/useLineItems'
import { useOrganization } from '@/hooks/useOrganization'
import { getCurrencyFormatter } from '@/lib/format'
import type { LineItemSuggestion } from '@/requests/line-items'

import type { RegistryRowValue } from './LineItemRow'

function toRegistryRowValue(
  suggestion: LineItemSuggestion,
): Omit<RegistryRowValue, 'id'> {
  return {
    type: 'registry',
    lineItemId: suggestion.id,
    title: suggestion.title,
    unitPrice: suggestion.unitPrice,
    unitType: suggestion.unitType,
    description: suggestion.description,
    taxable: suggestion.taxable,
    quantity: suggestion.quantity,
    discountType: suggestion.discountType,
    discount: suggestion.discount,
  }
}

const SUGGESTION_DEBOUNCE_MS = 200

const currencyFormatter = getCurrencyFormatter()

interface LineItemTitleComboboxProps {
  id?: string
  value: string
  onChange: (title: string) => void
  onSelectSuggestion: (suggestion: Omit<RegistryRowValue, 'id'>) => void
  autoFocus?: boolean
}

export function LineItemTitleCombobox({
  id,
  value,
  onChange,
  onSelectSuggestion,
  autoFocus,
}: LineItemTitleComboboxProps) {
  const [open, setOpen] = useState(false)
  const [activeId, setActiveId] = useState('')
  const [debouncedValue, setDebouncedValue] = useState(value)
  const listId = useId()
  const { data: suggestResult } = useLineItemSuggestionsQuery(debouncedValue)
  const { data: organizationResult } = useOrganization()
  const isGstRegistered = organizationResult?.data?.gstRegistered === true

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), SUGGESTION_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [value])

  const suggestions = suggestResult?.data ?? []
  const hasSuggestions = suggestions.length > 0 && value.length >= 2

  function handleSelect(suggestion: LineItemSuggestion) {
    onSelectSuggestion(toRegistryRowValue(suggestion))
    setOpen(false)
    setActiveId('')
  }

  const isOpen = open && hasSuggestions

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={value}
          autoFocus={autoFocus}
          placeholder="Search registry or type a new item"
          className="h-9 shadow-sm"
          onChange={(changeEvent) => {
            onChange(changeEvent.target.value)
            setOpen(changeEvent.target.value.length >= 2)
          }}
          onFocus={() => {
            if (value.length >= 2) setOpen(true)
          }}
          onKeyDown={(keyboardEvent) => {
            if (keyboardEvent.key === 'Escape') setOpen(false)
            if (!hasSuggestions) return
            if (keyboardEvent.key === 'ArrowDown' || keyboardEvent.key === 'ArrowUp') {
              keyboardEvent.preventDefault()
              setOpen(true)
              const step = keyboardEvent.key === 'ArrowDown' ? 1 : -1
              const currentIndex = suggestions.findIndex(({ id }) => id === activeId)
              const nextIndex =
                (currentIndex + step + suggestions.length) % suggestions.length
              setActiveId(suggestions[nextIndex].id)
            }
            if (keyboardEvent.key === 'Enter' && isOpen) {
              const active = suggestions.find(({ id }) => id === activeId)
              if (active) {
                keyboardEvent.preventDefault()
                handleSelect(active)
              }
            }
          }}
        />
      </PopoverAnchor>
      {hasSuggestions && (
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onOpenAutoFocus={(focusEvent) => focusEvent.preventDefault()}
          onInteractOutside={(interactEvent) => {
            if (interactEvent.target instanceof HTMLInputElement)
              interactEvent.preventDefault()
          }}
        >
          <Command shouldFilter={false} value={activeId} onValueChange={setActiveId}>
            <CommandList id={listId}>
              <CommandGroup>
                {suggestions.map((suggestion) => (
                  <CommandItem
                    key={suggestion.id}
                    value={suggestion.id}
                    onSelect={() => handleSelect(suggestion)}
                  >
                    <span className="flex-1 truncate">{suggestion.title}</span>
                    <span className="ml-3 shrink-0 text-sm font-medium tabular-nums">
                      {currencyFormatter.format(
                        computeLineItemSubtotal(
                          suggestion.unitPrice,
                          suggestion.quantity,
                          suggestion.discount,
                          suggestion.discountType,
                        ),
                      )}
                    </span>
                    {isGstRegistered && (
                      <span className="text-muted-foreground ml-2 shrink-0 text-xs">
                        {suggestion.taxable ? 'Taxable' : 'Non-taxable'}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  )
}
